import { useEffect, useRef, useState } from "react";
import { Calendar } from "../../assests/ExportImageIcon";
import "./DatePicker.css";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 100 }, (_, i) => CURRENT_YEAR - i);

const pad = (n) => String(n).padStart(2, "0");
const toIso = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const toDisplay = (date) => `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`;

const parseIso = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseDisplay = (text) => {
  const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  const [, mm, dd, yyyy] = match;
  const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (
    date.getFullYear() !== Number(yyyy) ||
    date.getMonth() !== Number(mm) - 1 ||
    date.getDate() !== Number(dd)
  ) {
    return null;
  }
  return date;
};

const isSameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const buildCalendarDays = (viewDate) => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const startOffset = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = Array(startOffset).fill(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(new Date(year, month, d));
  return days;
};

const DatePicker = ({ value, onChange, min, max, placeholder = "MM/DD/YYYY", className = "" }) => {
  const selectedDate = parseIso(value);

  const [text, setText] = useState(selectedDate ? toDisplay(selectedDate) : "");
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [viewDate, setViewDate] = useState(selectedDate || new Date());
  const wrapperRef = useRef(null);
  const minDate = min ? parseIso(min) : null;
  const maxDate = max ? parseIso(max) : null;

  useEffect(() => {
    const next = parseIso(value);
    setText(next ? toDisplay(next) : "");
    if (next) setViewDate(next);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Flip the calendar panel above the input when there isn't enough room
  // below (e.g. the field sits near the bottom of the viewport after
  // scrolling) — otherwise it opens off-screen / gets clipped.
  useEffect(() => {
    if (!open || !wrapperRef.current) return;
    const PANEL_HEIGHT = 360;
    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    setOpenUp(spaceBelow < PANEL_HEIGHT && spaceAbove > spaceBelow);
  }, [open]);

  const isDisabled = (date) => (maxDate && date > maxDate) || (minDate && date < minDate);

  const commitDate = (date) => {
    if (isDisabled(date)) return;
    onChange(toIso(date));
  };

  const handleTextChange = (e) => {
    const raw = e.target.value;
    setText(raw);
    const parsed = parseDisplay(raw);
    if (parsed) commitDate(parsed);
  };

  const handleDayClick = (date) => {
    commitDate(date);
    setOpen(false);
  };

  const goMonth = (offset) => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
  };

  const days = buildCalendarDays(viewDate);

  return (
    <div className={`datePicker ${className}`.trim()} ref={wrapperRef}>
      <input
        type="text"
        className="datePicker__input"
        placeholder={placeholder}
        value={text}
        onChange={handleTextChange}
        onFocus={() => setOpen(true)}
      />
      <button
        type="button"
        className="datePicker__iconBtn"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open calendar"
      >
        <img src={Calendar} alt="" width="16" height="16" />
      </button>

      {open && (
        <div className={`datePicker__panel ${openUp ? "datePicker__panel--up" : ""}`}>
          <div className="datePicker__header">
            <button type="button" className="datePicker__nav" onClick={() => goMonth(-1)} aria-label="Previous month">
              <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
                <path d="M7 1L2 6l5 5" stroke="#1D2A4D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            <select
              className="datePicker__select"
              value={viewDate.getMonth()}
              onChange={(e) => setViewDate(new Date(viewDate.getFullYear(), Number(e.target.value), 1))}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i}>
                  {m}
                </option>
              ))}
            </select>

            <select
              className="datePicker__select"
              value={viewDate.getFullYear()}
              onChange={(e) => setViewDate(new Date(Number(e.target.value), viewDate.getMonth(), 1))}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <button type="button" className="datePicker__nav" onClick={() => goMonth(1)} aria-label="Next month">
              <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
                <path d="M1 1l5 5-5 5" stroke="#1D2A4D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          <div className="datePicker__weekdays">
            {WEEKDAYS.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>

          <div className="datePicker__grid">
            {days.map((date, i) =>
              date ? (
                <button
                  key={i}
                  type="button"
                  className={
                    "datePicker__day" +
                    (isSameDay(date, selectedDate) ? " datePicker__day--selected" : "") +
                    (isSameDay(date, new Date()) ? " datePicker__day--today" : "")
                  }
                  disabled={isDisabled(date)}
                  onClick={() => handleDayClick(date)}
                >
                  {date.getDate()}
                </button>
              ) : (
                <span key={i} />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
